import React, { useEffect, useState } from 'react';
import { HiInformationCircle } from 'react-icons/hi';
import { Link } from 'react-router-dom';
import { Alert, Button, Spinner } from '../../lib';
import apiServices from '../../services/apiServices';
import {
  ArchiveResponseSnippet,
  GetResponseSnippets,
  ResponseSnippet,
  ResponseSnippetsResponse,
  SaveResponseSnippet,
} from '../../services/dtos';
import { fmtDateTime } from './supportShared';

// T6: the saved-answers library (vision Feature 3). Anyone working a lane
// can add or edit snippets — they're a shared toolbox, and consistency is
// the point ("it keeps our answers consistent no matter who's at the desk").
// Archive, never delete; edits are recorded in the audit trail.
const SupportSnippetsPage: React.FC = () => {
  const [data, setData] = useState<ResponseSnippetsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // editor state — id null = new snippet
  const [editing, setEditing] = useState<{ id: number | null } | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  useEffect(() => {
    apiServices.client
      .get(new GetResponseSnippets())
      .then((res: ResponseSnippetsResponse) => {
        setData(res);
        setLoading(false);
      })
      .catch((e: any) => {
        setError(e?.responseStatus?.message || 'Failed to load snippets');
        setLoading(false);
      });
  }, []);

  const openEditor = (s?: ResponseSnippet) => {
    setEditing({ id: s?.id ?? null });
    setTitle(s?.title || '');
    setBody(s?.body || '');
  };

  const save = async () => {
    if (busy || !title.trim() || !body.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const req = new SaveResponseSnippet();
      if (editing?.id != null) req.id = editing.id;
      req.title = title.trim();
      req.body = body.trim();
      setData(await apiServices.client.post(req));
      setEditing(null);
    } catch (e: any) {
      setError(e?.responseStatus?.message || 'Failed to save the snippet');
    } finally {
      setBusy(false);
    }
  };

  const archive = async (s: ResponseSnippet) => {
    if (busy) return;
    if (
      !window.confirm(
        `Archive "${s.title}"? It leaves the picker but stays in the record.`
      )
    )
      return;
    setBusy(true);
    setError(null);
    try {
      const req = new ArchiveResponseSnippet();
      req.id = s.id;
      setData(await apiServices.client.post(req));
    } catch (e: any) {
      setError(e?.responseStatus?.message || 'Failed to archive the snippet');
    } finally {
      setBusy(false);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center pt-10">
        <Spinner color="info" />
      </div>
    );

  const snippets = data?.snippets || [];
  const active = snippets.filter((s) => !s.archivedAtUtc);
  const archived = snippets.filter((s) => s.archivedAtUtc);

  const inputCls =
    'block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white';

  return (
    <div className="mx-auto w-full flex max-w-4xl flex-col gap-6 dark:text-white">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/help/queue" className="text-sm text-indigo-600 dark:text-indigo-400">
            ← Back to the queue
          </Link>
          <h2 className="mt-1 text-2xl font-bold">Saved Answers</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Canned responses for the questions that come up over and over.
            Drop one into a reply from the ticket page, then edit it to fit
            before sending.
          </p>
        </div>
        <Button size="sm" onClick={() => openEditor()}>
          New Snippet
        </Button>
      </div>

      {error && (
        <Alert color="failure" icon={HiInformationCircle}>
          {error}
        </Alert>
      )}

      {editing && (
        <div className="rounded-lg border border-indigo-200 dark:border-indigo-700 p-4 flex flex-col gap-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            {editing.id == null ? 'New snippet' : 'Edit snippet'}
          </h3>
          <input
            className={inputCls}
            placeholder="Title — how it appears in the picker"
            value={title}
            maxLength={200}
            onChange={(e) => setTitle(e.target.value)}
          />
          <textarea
            rows={5}
            className={inputCls}
            placeholder="The answer text…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <div className="flex gap-2">
            <Button onClick={save} disabled={busy || !title.trim() || !body.trim()}>
              {busy ? <Spinner size="sm" /> : 'Save'}
            </Button>
            <Button color="gray" onClick={() => setEditing(null)} disabled={busy}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        {active.length === 0 && !editing && (
          <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center text-sm text-gray-500 dark:text-gray-400">
            No saved answers yet — add the ones you type over and over.
          </div>
        )}
        {active.map((s) => (
          <div
            key={s.id}
            className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 flex flex-col gap-1"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-medium text-gray-900 dark:text-white">
                {s.title}
              </span>
              <div className="flex gap-2">
                <Button size="xs" color="gray" onClick={() => openEditor(s)} disabled={busy}>
                  Edit
                </Button>
                <Button size="xs" color="gray" onClick={() => archive(s)} disabled={busy}>
                  Archive
                </Button>
              </div>
            </div>
            <div className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
              {s.body}
            </div>
            <div className="text-xs text-gray-400 dark:text-gray-500">
              Added by {s.createdByName} {fmtDateTime(s.createdAtUtc)}
              {s.updatedAtUtc
                ? ` · edited by ${s.updatedByName} ${fmtDateTime(s.updatedAtUtc)}`
                : ''}
            </div>
          </div>
        ))}
      </div>

      {archived.length > 0 && (
        <div className="flex flex-col gap-2">
          <button
            className="text-left text-sm font-semibold text-gray-900 dark:text-white"
            onClick={() => setShowArchived(!showArchived)}
          >
            Archived ({archived.length}) {showArchived ? '▾' : '▸'}
          </button>
          {showArchived &&
            archived.map((s) => (
              <div
                key={s.id}
                className="rounded-lg border border-gray-100 dark:border-gray-800 p-3 text-xs text-gray-500 dark:text-gray-400"
              >
                <span className="font-medium">{s.title}</span> · archived by{' '}
                {s.archivedByName} {fmtDateTime(s.archivedAtUtc)}
                <div className="mt-1 whitespace-pre-wrap">{s.body}</div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default SupportSnippetsPage;
